import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import InitialsBadge from '../../../components/ui/InitialsBadge.jsx';

const TeamOverview = ({ teamMembers = [], userRole, onInviteMembers }) => {
  const navigate = useNavigate();
  const [activityStatusMap, setActivityStatusMap] = useState(new Map());
  const [loading, setLoading] = useState(true);

  // Fetch real activity status from backend
  const fetchActivityStatus = useCallback(async () => {
    try {
      const sessionService = (await import('../../../utils/sessionService.js')).default;
      const organizationId = sessionService.getOrganizationId?.();
      if (!organizationId) {
        setLoading(false);
        return;
      }

      const realApiService = (await import('../../../utils/realApiService.js')).default;
      const response = await realApiService.users.getActivityStatus(organizationId);

      if (response?.data && Array.isArray(response.data)) {
        const statusMap = new Map();
        response.data.forEach(userActivity => {
          if (userActivity.user_id) {
            statusMap.set(userActivity.user_id, {
              isActive: userActivity.is_active || false,
              status: userActivity.status || 'offline',
              lastActivity: userActivity.last_activity
            });
          }
        });
        setActivityStatusMap(statusMap);
      }
    } catch (error) {
      console.error('Failed to fetch activity status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Heartbeat and fetch activity on mount, refresh every 2 minutes
  useEffect(() => {
    let pollInterval;
    
    const updateAndFetch = async () => {
      try {
        const realApiService = (await import('../../../utils/realApiService.js')).default;
        await realApiService.users.updateActivity();
      } catch {
        // Silent fail for heartbeat
      }
      await fetchActivityStatus();
    };

    updateAndFetch();
    pollInterval = setInterval(updateAndFetch, 2 * 60 * 1000); // 2 minutes

    return () => pollInterval && clearInterval(pollInterval);
  }, [fetchActivityStatus]);

  // De-duplicate members and merge with activity data
  const members = useMemo(() => {
    const memberMap = new Map();
    
    (teamMembers || []).forEach(m => {
      const id = m?.id || m?.user_id || m?.email;
      if (!id || memberMap.has(id)) return;

      const activity = activityStatusMap.get(id) || activityStatusMap.get(m?.user_id);
      
      memberMap.set(id, {
        ...m,
        isOnline: activity?.isActive || false,
        status: activity?.status || 'offline',
        lastActivity: activity?.lastActivity || m.lastActivity || m.last_active_at || m.updated_at
      });
    });

    return Array.from(memberMap.values());
  }, [teamMembers, activityStatusMap]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = members.length;
    const online = members.filter(m => m.isOnline || m.status === 'online').length;
    const away = members.filter(m => m.status === 'away').length;
    
    // Active = online + away (active within 30 minutes)
    const active = online + away;

    return { total, online, active };
  }, [members]);

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return null;
    
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return null;

    const diff = Date.now() - date.getTime();
    if (diff < 0) return 'just now';
    if (diff > 365 * 24 * 60 * 60 * 1000) return 'long ago';

    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;

    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  };

  // Get status display
  const getStatusDisplay = (member) => {
    if (member.isOnline || member.status === 'online') {
      return { text: 'Online', color: 'text-emerald-500', dot: 'bg-emerald-500' };
    }
    if (member.status === 'away') {
      const timeAgo = formatTimeAgo(member.lastActivity);
      return { 
        text: timeAgo ? `Away • ${timeAgo}` : 'Away', 
        color: 'text-amber-500', 
        dot: 'bg-amber-500' 
      };
    }
    const timeAgo = formatTimeAgo(member.lastActivity);
    return { 
      text: timeAgo ? `Offline • ${timeAgo}` : 'Offline', 
      color: 'text-slate-400', 
      dot: 'bg-slate-400' 
    };
  };

  // Handle member click
  const handleMemberClick = (member) => {
    if (userRole === 'admin' || userRole === 'owner') {
      const memberId = member?.id || member?.user_id;
      if (memberId) {
        navigate('/team-members', { state: { openMemberId: memberId } });
      }
    }
  };

  const canClick = userRole === 'admin' || userRole === 'owner';

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg border border-border dark:border-slate-800 p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Team Overview</h3>
        {onInviteMembers && (userRole === 'admin' || userRole === 'owner') && (
          <button 
            onClick={onInviteMembers}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
          >
            <Icon name="UserPlus" size={14} />
            Invite
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700 text-center">
          <div className="text-xl font-bold text-slate-800 dark:text-slate-100">{stats.total}</div>
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Total</div>
        </div>
        <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800/50 text-center">
          <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{stats.online}</div>
          <div className="text-xs font-medium text-emerald-600/70 dark:text-emerald-400/70">Online</div>
        </div>
        <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800/50 text-center">
          <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{stats.active}</div>
          <div className="text-xs font-medium text-blue-600/70 dark:text-blue-400/70" title="Active within 30 minutes">Active</div>
        </div>
      </div>

      {/* Members list - Compact 2-column grid */}
      <div className="flex-1 overflow-y-auto min-h-0 pr-1">
        {loading ? (
          // Loading skeleton - 2 column
          <div className="grid grid-cols-2 gap-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-2 p-2 animate-pulse">
                <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700" />
                <div className="flex-1 min-w-0">
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-1" />
                  <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded w-10" />
                </div>
              </div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-6 text-slate-500 dark:text-slate-400 text-sm">
            No team members yet
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-1.5">
            {members.slice(0, 10).map((member) => {
              const name = member?.name || member?.displayName || member?.email || 'Member';
              const role = member?.role || 'member';
              const statusDisplay = getStatusDisplay(member);

              // Role badge colors
              const roleColors = {
                owner: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
                admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
                member: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
              };

              return (
                <div 
                  key={member?.id || member?.user_id || name} 
                  className={`flex items-center gap-2 p-1.5 rounded-lg transition-all duration-200 ${
                    canClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 group' : ''
                  }`}
                  onClick={() => handleMemberClick(member)}
                  title={`${name} • ${statusDisplay.text}`}
                >
                  <div className="relative flex-shrink-0">
                    <InitialsBadge name={name} size={28} />
                    {/* Status dot */}
                    <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 ${statusDisplay.dot} rounded-full border-2 border-white dark:border-slate-900`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium text-xs truncate text-slate-700 dark:text-slate-200 ${canClick ? 'group-hover:text-blue-600 dark:group-hover:text-blue-400' : ''} transition-colors leading-tight`}>
                      {name.split(' ')[0]}
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium capitalize ${roleColors[role.toLowerCase()] || roleColors.member}`}>
                        {role}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* View all link */}
      {members.length > 10 && (
        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
          <button
            onClick={() => navigate('/team-members')}
            className="w-full text-center text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium py-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
          >
            View all {members.length} members →
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamOverview;
